import React from 'react';
import { useTranslation } from '../../../i18n';

interface EvaluationStatusProps {
  status: string;
  message?: string;
  onRefresh?: (forceEval?: boolean) => void | Promise<void>;
}

export const EvaluationStatus: React.FC<EvaluationStatusProps> = ({
  status,
  message,
  onRefresh,
}) => {
  const { t } = useTranslation();
  const isPending = ['SCORING', 'EVALUATING', 'SUBMITTING'].includes(status);
  const [isChecking, setIsChecking] = React.useState(false);

  const handleRefreshClick = async () => {
    if (!onRefresh || isChecking) return;
    setIsChecking(true);
    try {
      await onRefresh(true);
    } finally {
      setIsChecking(false);
    }
  };

  const getLocalizedMessage = (msg?: string) => {
    if (!msg) {
      return t('scorecard.eval_processing_desc', undefined, 'The AI evaluation engine is analyzing your conversation transcript against competency rubrics and evidence benchmarks.');
    }
    const lower = msg.toLowerCase();
    if (lower.includes('scoring and evaluating') || lower.includes('evaluating your attempt')) {
      return t('scorecard.scoring_evaluating_msg', undefined, 'Scoring and evaluating your attempt performance...');
    }
    if (lower.includes('results are generating') || lower.includes('generating')) {
      return t('scorecard.results_generating_msg', undefined, 'Attempt evaluation results are generating...');
    }
    return msg;
  };

  return (
    <div style={{ maxWidth: '560px', margin: '40px auto', padding: '36px', background: 'var(--white)', borderRadius: '14px', border: '1px solid var(--line)', textAlign: 'center', display: 'grid', gap: '14px', boxShadow: '0 1px 3px rgba(0,0,0,0.02)' }}>
      <div style={{ display: 'flex', justifyContent: 'center' }}>
        <div className="dashboard-spinner" style={{ width: '36px', height: '36px', borderWidth: '3px' }} />
      </div>

      <h2 style={{ fontFamily: 'var(--display)', fontSize: '20px', fontWeight: 600, color: 'var(--navy)', margin: 0 }}>
        {isPending ? t('scorecard.scoring_dialogue_title', undefined, 'Scoring Simulation Dialogue…') : t('scorecard.eval_processing_title', undefined, 'Evaluation Processing')}
      </h2>

      <p style={{ fontSize: '13.5px', color: 'var(--muted)', margin: 0, lineHeight: 1.5 }}>
        {getLocalizedMessage(message)}
      </p>

      {onRefresh && (
        <div style={{ paddingTop: '8px' }}>
          <button
            type="button"
            onClick={handleRefreshClick}
            disabled={isChecking}
            className="admin-btn admin-btn--primary admin-btn--sm"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              padding: '8px 18px',
              fontSize: '13px',
              fontWeight: 600,
              cursor: isChecking ? 'not-allowed' : 'pointer',
              opacity: isChecking ? 0.8 : 1,
            }}
          >
            {isChecking && (
              <span className="dashboard-spinner" style={{ width: '14px', height: '14px', borderWidth: '2px', borderColor: '#ffffff', borderTopColor: 'transparent' }} />
            )}
            <span>{isChecking ? t('scorecard.evaluating_in_progress', undefined, 'Evaluating Transcript...') : t('scorecard.check_status_btn', undefined, 'Check Evaluation Status')}</span>
          </button>
        </div>
      )}
    </div>
  );
};
